import {
  bearerAuth,
  customAuth,
  dropUnsetTokenResponseFields,
  oauthAuth,
} from "../standardAuthPatterns";
import { caCertificateItem, defineConnector } from "../types";
import logo from "./logo.svg";

const authModeTarget = {
  kind: "configuration" as const,
  field: "authMode",
} as const;

export const remoteMcp = defineConnector({
  identifier: "remoteMcp",
  // The agent system prompt tells the model to search 'MCP server' verbatim to
  // find this connector by kind — keep that substring in `name` if you rename it.
  name: "MCP server",
  tagLine: "Connect to a remote MCP server",
  logo,
  protocol: "http",
  tags: ["custom"],
  customizeStepHandledInline: true,
  defaultUrlPattern: ({ serverUrl }) =>
    serverUrl && URL.canParse(serverUrl)
      ? `${new URL(serverUrl).origin.replace(/\./g, "\\.")}/.*`
      : "",
  auth: (app) => {
    switch (app.configuration.authMode) {
      case "oauth":
        return oauthAuth;
      case "none":
        return customAuth("noop");
      default:
        return bearerAuth(app.secret.token);
    }
  },
  // Discovery persists endpoints into configuration under keys that aren't
  // form fields, hence the Record casts.
  oauth: {
    grantType: () => "authorization_code",
    clientCredentialsFrom: "dcr",
    discoverFrom: (cfg) => cfg.serverUrl ?? "",
    authorizationEndpoint: (cfg) =>
      (cfg as Record<string, string>).authorizeUrl ?? "",
    tokenEndpoint: (cfg) => (cfg as Record<string, string>).tokenUrl ?? "",
    issuer: (cfg) => (cfg as Record<string, string>).issuer ?? "",
    tokenEndpointAuthMethod: (_cfg, { hasSecret }) =>
      hasSecret ? "client_secret_post" : "none",
    pkce: () => true,
    resource: (cfg) =>
      (cfg as Record<string, string>).resource ?? cfg.serverUrl ?? "",
    scopes: (cfg) =>
      ((cfg as Record<string, string>).discoveredScopes ?? "")
        .split(/\s+/)
        .filter(Boolean),
    normalizeTokenResponse: dropUnsetTokenResponseFields,
  },
  steps: [
    {
      title: "Connect to your MCP server",
      items: [
        {
          kind: "markdown",
          content:
            "Connects to any remote MCP (Model Context Protocol) server that speaks streamable HTTP. Authenticate with OAuth — the connection is discovered and registered automatically — paste a bearer token, or skip authentication.",
        },
        {
          kind: "input",
          label: "Name",
          placeholder: "e.g. My MCP server",
          required: true,
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "url",
          label: "Server URL",
          placeholder: "https://mcp.example.com/mcp",
          required: true,
          helperText: "The endpoint that accepts MCP JSON-RPC requests.",
          target: { kind: "configuration", field: "serverUrl" },
        },
        {
          kind: "select",
          label: "Authentication",
          target: authModeTarget,
          defaultValue: "oauth",
          options: [
            { value: "bearer", label: "Bearer token" },
            { value: "oauth", label: "OAuth" },
            { value: "none", label: "None" },
          ],
        },
        {
          kind: "input",
          label: "Bearer token",
          placeholder: "Paste your token or API key",
          required: true,
          helperText:
            "Sent as “Authorization: Bearer <token>” on every request.",
          target: { kind: "secrets", field: "token" },
          visibleWhen: [{ target: authModeTarget, value: "bearer" }],
        },
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Leave blank to register automatically",
          helperText:
            "Only needed when the server doesn’t support automatic client registration.",
          target: { kind: "oauthConnection", field: "clientId" },
          visibleWhen: [{ target: authModeTarget, value: "oauth" }],
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Leave blank for a public client",
          helperText:
            "Only needed when the client you registered manually has a secret.",
          target: { kind: "oauthConnection", field: "clientSecret" },
          visibleWhen: [{ target: authModeTarget, value: "oauth" }],
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/remoteMcp/callback",
          visibleWhen: [{ target: authModeTarget, value: "oauth" }],
        },
        {
          kind: "url-pattern",
          target: { kind: "connectedApp", field: "urlPattern" },
        },
        caCertificateItem,
        {
          kind: "oauth",
          dependsOn: [{ kind: "configuration", field: "serverUrl" }],
          visibleWhen: [{ target: authModeTarget, value: "oauth" }],
        },
      ],
    },
  ],
  usageHint: ({ serverUrl }) =>
    serverUrl
      ? `This is a remote MCP server at ${serverUrl}, speaking JSON-RPC 2.0 over streamable HTTP. Load the mcp-client skill for the handshake and tool-calling protocol; authentication is injected automatically.`
      : undefined,
  connectionTest: {
    protocol: "http",
    request: ({ serverUrl }) => ({
      url: serverUrl ?? "",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "initialize",
        params: {
          protocolVersion: "2025-06-18",
          capabilities: {},
          clientInfo: { name: "3B", version: "1.0" },
        },
      }),
      expectedBodyContains: "jsonrpc",
    }),
  },
});
